import { useEffect, useRef } from 'react';

/**
 * The body's scroll lock, shared by both Modal implementations — they nest (the
 * clip preview holds Add Clips), and a save/restore pair each strands the page:
 * the inner captures the outer's `hidden` and puts it back on the way out.
 *
 * Holders rather than an owner, because effects run child-first: the first to
 * acquire is the innermost dialog, which would hand the page back while the one
 * it opened from is still up. A set rather than a count, so a double release is
 * a no-op instead of a -1 that silently disables every later lock.
 */
const holders = new Set<symbol>();
let releasedOverflow = '';

function acquire(token: symbol): void {
  if (holders.size === 0) {
    releasedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  holders.add(token);
}

function release(token: symbol): void {
  if (!holders.delete(token)) return;
  if (holders.size === 0) {
    document.body.style.overflow = releasedOverflow;
    releasedOverflow = '';
  }
}

/** Locks body scroll while `active`, releasing on unmount. */
export function useBodyScrollLock(active: boolean): void {
  // One token per instance, so a re-render cannot register a second holder.
  const token = useRef(Symbol('body-scroll-lock'));

  useEffect(() => {
    if (!active) return;
    const held = token.current;
    acquire(held);
    return () => release(held);
  }, [active]);
}
